/**
 * Place nodes on rings around anchor points for the relationship graph.
 *
 * @param {{x: number, y: number}} anchorPosition
 * @param {!Array<string>} childKeys
 * @param {number} radius
 * @param {number} [startAngle]
 * @param {number} [arcRadians]
 * @return {!Object<string, {x: number, y: number}>}
 */
export function radialRingPositions(
  anchorPosition,
  childKeys,
  radius,
  startAngle = -Math.PI / 2,
  arcRadians = Math.PI * 2,
) {
  if (!childKeys.length) {
    return {};
  }
  const step = childKeys.length === 1 ? 0 : arcRadians / childKeys.length;
  /** @type {Object<string, {x: number, y: number}>} */
  const positions = {};
  childKeys.forEach((key, index) => {
    const angle = startAngle + index * step + (childKeys.length === 1 ? 0 : step / 2);
    positions[key] = {
      x: anchorPosition.x + radius * Math.cos(angle),
      y: anchorPosition.y + radius * Math.sin(angle),
    };
  });
  return positions;
}

function ringRadiusForCount(count, base) {
  return base + Math.min(Math.max(count - 6, 0) * 6, 72);
}

function outwardAngle(parentPosition, graphCenter) {
  return Math.atan2(parentPosition.y - graphCenter.y, parentPosition.x - graphCenter.x);
}

/**
 * Build positions for the merged graph from edges and a root key.
 *
 * @param {string} rootKey
 * @param {!Array<{source: string, target: string}>} edges
 * @param {!Set<string>} expandedKeys
 * @return {!Object<string, {x: number, y: number}>}
 */
export function layoutRelationshipGraph(rootKey, edges, expandedKeys) {
  const center = { x: 480, y: 340 };
  /** @type {Object<string, {x: number, y: number}>} */
  const positions = { [rootKey]: center };
  const bySource = edges.reduce((acc, edge) => {
    if (!acc[edge.source]) {
      acc[edge.source] = [];
    }
    acc[edge.source].push(edge.target);
    return acc;
  }, /** @type {Object<string, string[]>} */ ({}));

  const rootChildren = bySource[rootKey] || [];
  Object.assign(
    positions,
    radialRingPositions(
      center,
      rootChildren,
      ringRadiusForCount(rootChildren.length, 210),
    ),
  );

  expandedKeys.forEach(sourceKey => {
    if (sourceKey === rootKey || !positions[sourceKey]) {
      return;
    }
    const children = (bySource[sourceKey] || []).filter(key => positions[key] === undefined);
    if (!children.length) {
      return;
    }
    const parentPos = positions[sourceKey];
    const angle = outwardAngle(parentPos, center);
    const spread = Math.min(Math.PI * 1.35, Math.PI / 2 + children.length * 0.22);
    Object.assign(
      positions,
      radialRingPositions(parentPos, children, 150, angle - spread / 2, spread),
    );
  });

  return positions;
}
